import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Check,
  Crown,
  Zap,
  Building2,
  Sparkles,
  Euro,
  CreditCard,
  FileText,
  Video,
  Calculator,
  ChevronRight,
  Users,
  Map,
  Calendar,
  CloudSun,
  Warehouse,
  Home,
  Wrench,
  Beef,
  Target,
  Truck,
  Package,
  Scale,
  Bot,
  Plug,
  Volume2,
  ExternalLink,
  Download,
  Send,
  Plus,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for modules
const MODULE_ICONS = {
  'map': Map,
  'map-pin': Map,
  'calendar': Calendar,
  'cloud-sun': CloudSun,
  'warehouse': Warehouse,
  'home': Home,
  'wrench': Wrench,
  'beef': Beef,
  'euro': Euro,
  'users': Users,
  'target': Target,
  'truck': Truck,
  'package': Package,
  'calculator': Calculator,
  'scale': Scale,
  'bot': Bot,
  'plug': Plug,
  'volume-2': Volume2,
};

// Pricing Card Component
const PricingCard = ({ plan, isCurrentPlan, onSubscribe }) => {
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (period) => {
    setLoading(true);
    await onSubscribe(plan.id, period);
    setLoading(false);
  };

  return (
    <Card className={`relative flex flex-col ${plan.populaire ? 'border-primary shadow-lg scale-105' : ''}`}>
      {plan.populaire && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
          Populaire
        </Badge>
      )}
      
      <CardHeader className="text-center pb-2">
        <div 
          className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
          style={{ backgroundColor: `${plan.couleur}20` }}
        >
          {plan.id === 'starter' && <Zap className="w-6 h-6" style={{ color: plan.couleur }} />}
          {plan.id === 'agriculteur' && <Building2 className="w-6 h-6" style={{ color: plan.couleur }} />}
          {plan.id === 'professionnel' && <Crown className="w-6 h-6" style={{ color: plan.couleur }} />}
          {plan.id === 'entreprise' && <Sparkles className="w-6 h-6" style={{ color: plan.couleur }} />}
        </div>
        <CardTitle>{plan.nom}</CardTitle>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1">
        {/* Prix */}
        <div className="text-center mb-6">
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl font-bold">{plan.prix_mensuel}</span>
            <span className="text-muted-foreground">€/mois</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            ou {plan.prix_annuel}€/an (2 mois offerts)
          </p>
        </div>

        {/* Limites */}
        <div className="space-y-2 mb-6">
          <div className="flex items-center justify-between text-sm">
            <span>Surface max</span>
            <span className="font-medium">
              {plan.surface_max === -1 ? 'Illimité' : `${plan.surface_max} ha`}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>Sociétés</span>
            <span className="font-medium">
              {plan.societes_max === -1 ? 'Illimité' : plan.societes_max}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>Utilisateurs</span>
            <span className="font-medium">
              {plan.utilisateurs_max === -1 ? 'Illimité' : plan.utilisateurs_max}
            </span>
          </div>
        </div>

        {/* Blocs inclus */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Blocs inclus :</p>
          <div className="flex flex-wrap gap-1">
            {plan.blocs_inclus.map((bloc) => (
              <Badge key={bloc} variant="secondary" className="text-xs capitalize">
                {bloc}
              </Badge>
            ))}
          </div>
        </div>

        {/* Features spéciales */}
        {(plan.support_prioritaire || plan.visio_onboarding) && (
          <div className="mt-4 pt-4 border-t space-y-2">
            {plan.support_prioritaire && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="w-4 h-4" />
                Support prioritaire
              </div>
            )}
            {plan.visio_onboarding && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="w-4 h-4" />
                Visio d'onboarding offerte
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-col gap-2">
        {isCurrentPlan ? (
          <Button disabled className="w-full" variant="outline">
            <Check className="w-4 h-4 mr-2" />
            Plan actuel
          </Button>
        ) : (
          <>
            <Button 
              onClick={() => handleSubscribe('monthly')} 
              disabled={loading}
              className="w-full"
              style={{ backgroundColor: plan.couleur }}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              S'abonner
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

// Main Billing Page
const BillingPage = () => {
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for Stripe callback
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (urlParams.get('success') === 'true' && sessionId) {
      // Poll payment status
      pollPaymentStatus(sessionId);
    }
    if (urlParams.get('canceled') === 'true') {
      toast.info('Paiement annulé');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Poll payment status after Stripe redirect
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Vérification du paiement en cours. Veuillez vérifier votre email de confirmation.');
      setCheckingPayment(false);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    setCheckingPayment(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/stripe/checkout-status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur vérification statut');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Votre abonnement est maintenant actif.');
        setCheckingPayment(false);
        window.history.replaceState({}, '', window.location.pathname);
        fetchData(); // Recharger les données
        return;
      } else if (data.status === 'expired') {
        toast.error('Session de paiement expirée. Veuillez réessayer.');
        setCheckingPayment(false);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Erreur polling:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [plansRes, modulesRes, subRes, invoicesRes, quotesRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/plans`).then(r => r.json()),
        fetch(`${API_URL}/api/billing/modules`).then(r => r.json()),
        fetch(`${API_URL}/api/billing/subscription`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/billing/invoices`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/billing/quotes`, { headers }).then(r => r.json()),
      ]);

      setPlans(plansRes.plans || []);
      setModules(modulesRes);
      setSubscription(subRes.subscription);
      setInvoices(invoicesRes.invoices || []);
      setQuotes(quotesRes.quotes || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId, period) => {
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await fetch(
        `${API_URL}/api/billing/stripe/create-checkout?plan_id=${planId}&billing_period=${period}&origin_url=${encodeURIComponent(originUrl)}`, 
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      
      if (data.success && data.checkout_url) {
        toast.info('Redirection vers Stripe...');
        window.location.href = data.checkout_url;
      } else if (data.detail) {
        toast.error(data.detail);
      } else {
        // Fallback: créer l'abonnement directement (sans Stripe)
        const subResponse = await fetch(`${API_URL}/api/billing/subscription`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            plan_id: planId,
            billing_period: period,
            payment_method: 'card'
          })
        });

        if (subResponse.ok) {
          toast.success('Abonnement créé (mode démo)');
          fetchData();
        }
      }
    } catch (error) {
      console.error('Erreur subscription:', error);
      toast.error('Erreur lors de la souscription');
    }
  };

  const createVisioRoom = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/billing/visio/create-room`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.join_url) {
        window.open(data.join_url, '_blank');
        toast.success('Salle de visio créée');
      }
    } catch (error) {
      toast.error('Erreur création visio');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Payment verification overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-lg font-semibold">Vérification du paiement...</p>
            <p className="text-muted-foreground">Veuillez patienter</p>
          </Card>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tarifs & Facturation</h1>
          <p className="text-muted-foreground">
            Gérez votre abonnement et vos factures
          </p>
        </div>
        <Button onClick={createVisioRoom} variant="outline">
          <Video className="w-4 h-4 mr-2" />
          Créer une visio
        </Button>
      </div>

      <Tabs defaultValue="plans" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="plans">Plans & Tarifs</TabsTrigger>
          <TabsTrigger value="subscription">Mon Abonnement</TabsTrigger>
          <TabsTrigger value="invoices">Factures</TabsTrigger>
          <TabsTrigger value="quotes">Devis</TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          {/* Pricing Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={subscription?.plan_id === plan.id}
                onSubscribe={handleSubscribe}
              />
            ))}
          </div>

          {/* Modules Detail */}
          <Card>
            <CardHeader>
              <CardTitle>Détail des Modules</CardTitle>
              <CardDescription>
                Tous les modules disponibles par catégorie
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(modules.by_category || {}).map(([cat, mods]) => (
                  <div key={cat} className="space-y-3">
                    <h3 className="font-semibold capitalize flex items-center gap-2">
                      <Badge variant="outline">{modules.categories?.[cat]?.nom || cat}</Badge>
                    </h3>
                    <div className="space-y-2">
                      {mods.map((mod) => {
                        const Icon = MODULE_ICONS[mod.icon] || Package;
                        return (
                          <div key={mod.id} className="flex items-center gap-2 text-sm">
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <span>{mod.nom}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Billing Entity Info */}
          <Card>
            <CardHeader>
              <CardTitle>Entité de Facturation</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="font-semibold">EARL Conversion Agri</p>
              <p className="text-muted-foreground">Route des Vignes, 84000 Avignon</p>
              <p className="text-muted-foreground">SIRET: XXX XXX XXX XXXXX</p>
              <p className="text-muted-foreground">TVA Intra: FR XX XXX XXX XXX</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-4">
          {subscription ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Plan {subscription.plan_details?.nom || subscription.plan_id}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Montant</p>
                    <p className="text-2xl font-bold">{subscription.amount}€</p>
                    <p className="text-sm text-muted-foreground">
                      /{subscription.billing_period === 'annual' ? 'an' : 'mois'}
                    </p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Statut</p>
                    <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                      {subscription.status === 'active' ? 'Actif' : subscription.status}
                    </Badge>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Prochain paiement</p>
                    <p className="font-medium">
                      {new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Modules actifs</h4>
                  <div className="flex flex-wrap gap-2">
                    {subscription.plan_details?.modules_inclus?.map((mod) => (
                      <Badge key={mod} variant="secondary">{mod}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="text-center p-8">
              <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun abonnement actif</h3>
              <p className="text-muted-foreground mb-4">
                Choisissez un plan pour accéder à toutes les fonctionnalités
              </p>
              <Button onClick={() => document.querySelector('[value="plans"]').click()}>
                Voir les plans
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Factures</h3>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle facture
            </Button>
          </div>

          {invoices.length > 0 ? (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <Card key={invoice.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <FileText className="w-8 h-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {invoice.client?.nom || 'Client'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{invoice.total}€</p>
                        <Badge variant={
                          invoice.status === 'paid' ? 'default' :
                          invoice.status === 'sent' ? 'secondary' : 'outline'
                        }>
                          {invoice.status === 'paid' ? 'Payée' :
                           invoice.status === 'sent' ? 'Envoyée' : 'Brouillon'}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center p-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune facture</p>
            </Card>
          )}
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Devis</h3>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devis
            </Button>
          </div>

          {quotes.length > 0 ? (
            <div className="space-y-2">
              {quotes.map((quote) => (
                <Card key={quote.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-medium">{quote.quote_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {quote.client?.nom || 'Client'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{quote.total}€</p>
                        <p className="text-xs text-muted-foreground">
                          Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center p-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun devis</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingPage;
